/**
 * Lightweight navigation guard. The interview page installs a handler
 * while a live session is running so that any in-app navigation
 * (launchpad sidebar, header links, header brand, etc.) can show an
 * exit-confirmation modal instead of silently dropping the session.
 *
 * Usage:
 *   import { requestNavigation, installNavigationGuard } from '../utils/navigationGuard';
 *
 *   // in callers (sidebar, header, etc.):
 *   requestNavigation(path, navigate);   // → either navigates or asks the guard
 *
 *   // in the interview page:
 *   useEffect(() => {
 *     if (!interviewStarted) return;
 *     return installNavigationGuard((path, navigate) => {
 *       setPendingExit({ path, navigate });
 *       setShowExitConfirm(true);
 *     });
 *   }, [interviewStarted]);
 */
let guardFn = null;

export const installNavigationGuard = (fn) => {
  guardFn = fn;
  return () => {
    if (guardFn === fn) guardFn = null;
  };
};

export const requestNavigation = (path, navigate) => {
  if (typeof guardFn === 'function') {
    guardFn(path, navigate);
    return true; // intercepted
  }
  navigate(path);
  return false;
};

export const hasNavigationGuard = () => typeof guardFn === 'function';
